/**
 * TOUTES LES ICÔNES DE L'APPLICATION PASSENT PAR ICI — jamais par les bibliothèques directement.
 *
 * POURQUOI CE DÉTOUR. La taille d'une icône est une PROP (`size={24}`), pas une feuille de style :
 * elle échappe donc au facteur d'échelle du thème, qui ne touche que `typography`, `spacing` et
 * `radius`. Sur grand écran, le texte rétrécissait et les icônes non — visible à l'œil dans la
 * barre d'onglets, où des pictogrammes de 24 px surplombaient des libellés de 9.
 *
 * POURQUOI PAS UN BALAYAGE DES APPELS. Réécrire les 112 `size={N}` du dépôt aurait paru plus
 * simple, et aurait été faux : `size` sert AUSSI à `IconPill` (40), `BrandLogo` (44), `ScoreRing`
 * (72), `GoogleLogo` (20) et `Sparkle` (16), qui ne sont pas des icônes et dont certains dérivent
 * leur géométrie interne de ce nombre. Envelopper les bibliothèques est le seul endroit où
 * « icône » est sans ambiguïté — et les 112 appels restent lisibles tels quels.
 *
 * Sur mobile, `taille()` rend son argument inchangé : aucune icône ne bouge d'un pixel.
 */
import React from 'react';
import {
  Ionicons as IoniconsBase,
  MaterialCommunityIcons as MaterialCommunityIconsBase,
} from '@expo/vector-icons';
import * as Lucide from 'lucide-react-native';
import type { LucideIcon } from 'lucide-react-native';
import { taille } from '../theme/tokens';

export type { LucideIcon };

/** Taille par défaut des deux bibliothèques, reprise telle quelle pour ne rien changer d'autre. */
const TAILLE_PAR_DEFAUT = 24;

/**
 * `forwardRef`, ET PAS UN SIMPLE COMPOSANT-FONCTION. Deux raisons, dont une que les tests ont
 * attrapée : une icône lucide EST un objet `forwardRef`, et `data/notifications` en transporte
 * comme valeurs (`typeof n.icon === 'object'`). Une enveloppe-fonction change ce type sous les
 * pieds des appelants. L'autre raison est qu'une enveloppe naïve avale silencieusement la `ref` —
 * personne n'en passe aujourd'hui, et c'est exactement le genre de régression qui ne se découvre
 * que le jour où quelqu'un essaie.
 */
/**
 * Le type `LucideIcon` exporté par la bibliothèque est `ForwardRefExoticComponent<LucideProps>`,
 * SANS `RefAttributes` — alors que `createLucideIcon` produit bel et bien un composant qui accepte
 * une `ref`. C'est un trou dans leurs typages, pas dans leur comportement. Cet alias le comble, et
 * il dit exactement ce qu'il ajoute.
 */
type IconeAvecRef = React.ForwardRefExoticComponent<
  React.ComponentProps<LucideIcon> & React.RefAttributes<unknown>
>;

function echelonnerLucide(Icone: LucideIcon): LucideIcon {
  const Rendue = Icone as IconeAvecRef;
  const Enveloppe = React.forwardRef<unknown, React.ComponentProps<LucideIcon>>(
    ({ size = TAILLE_PAR_DEFAUT, ...reste }, ref) => (
      <Rendue
        ref={ref}
        size={taille(typeof size === 'number' ? size : TAILLE_PAR_DEFAUT)}
        {...reste}
      />
    ),
  );
  Enveloppe.displayName = `Echelle(${Icone.displayName ?? 'Icon'})`;
  return Enveloppe as unknown as LucideIcon;
}

/**
 * DEUX ENVELOPPES ÉCRITES À LA MAIN, et non une fabrique générique.
 *
 * Chaque famille a son propre jeu de noms de glyphes — 1300 pour Ionicons, autant pour Material.
 * Une fabrique générique impose un seul type de props aux deux, et `name="check-decagram"` se
 * retrouve validé contre le vocabulaire d'Ionicons : quinze erreurs de compilation pour des icônes
 * parfaitement valides. Deux cas ne valaient pas une abstraction.
 *
 * `glyphMap` EST RECOPIÉ SUR L'ENVELOPPE, et ce n'est pas décoratif : `BottomNav` et
 * `CarriereScreen` écrivent `keyof typeof Ionicons.glyphMap` pour typer leurs noms d'icônes.
 * Sans lui, l'enveloppe compilerait mais ces deux types s'effondreraient.
 */
const EnveloppeIonicons = ({
  size = TAILLE_PAR_DEFAUT,
  ...reste
}: React.ComponentProps<typeof IoniconsBase>) => <IoniconsBase size={taille(size)} {...reste} />;

const EnveloppeMaterial = ({
  size = TAILLE_PAR_DEFAUT,
  ...reste
}: React.ComponentProps<typeof MaterialCommunityIconsBase>) => (
  <MaterialCommunityIconsBase size={taille(size)} {...reste} />
);

export const Ionicons = Object.assign(EnveloppeIonicons, { glyphMap: IoniconsBase.glyphMap });
export const MaterialCommunityIcons = Object.assign(EnveloppeMaterial, {
  glyphMap: MaterialCommunityIconsBase.glyphMap,
});

export const ArrowLeft = echelonnerLucide(Lucide.ArrowLeft);
export const ArrowUpCircle = echelonnerLucide(Lucide.ArrowUpCircle);
export const BadgeCheck = echelonnerLucide(Lucide.BadgeCheck);
export const Bell = echelonnerLucide(Lucide.Bell);
export const BellOff = echelonnerLucide(Lucide.BellOff);
export const Bookmark = echelonnerLucide(Lucide.Bookmark);
export const Briefcase = echelonnerLucide(Lucide.Briefcase);
export const Building2 = echelonnerLucide(Lucide.Building2);
export const CalendarCheck = echelonnerLucide(Lucide.CalendarCheck);
export const CalendarClock = echelonnerLucide(Lucide.CalendarClock);
export const CalendarDays = echelonnerLucide(Lucide.CalendarDays);
export const Camera = echelonnerLucide(Lucide.Camera);
export const Check = echelonnerLucide(Lucide.Check);
export const ChevronDown = echelonnerLucide(Lucide.ChevronDown);
export const ChevronRight = echelonnerLucide(Lucide.ChevronRight);
export const CircleAlert = echelonnerLucide(Lucide.CircleAlert);
export const CircleCheck = echelonnerLucide(Lucide.CircleCheck);
export const ClipboardCheck = echelonnerLucide(Lucide.ClipboardCheck);
export const ClipboardType = echelonnerLucide(Lucide.ClipboardType);
export const CreditCard = echelonnerLucide(Lucide.CreditCard);
export const ExternalLink = echelonnerLucide(Lucide.ExternalLink);
export const ArrowRight = echelonnerLucide(Lucide.ArrowRight);
export const Eye = echelonnerLucide(Lucide.Eye);
export const EyeOff = echelonnerLucide(Lucide.EyeOff);
export const FileText = echelonnerLucide(Lucide.FileText);
export const FolderOpen = echelonnerLucide(Lucide.FolderOpen);
export const Heart = echelonnerLucide(Lucide.Heart);
export const HelpCircle = echelonnerLucide(Lucide.HelpCircle);
export const Image = echelonnerLucide(Lucide.Image);
export const Lightbulb = echelonnerLucide(Lucide.Lightbulb);
export const Link2 = echelonnerLucide(Lucide.Link2);
export const Pencil = echelonnerLucide(Lucide.Pencil);
export const Globe = echelonnerLucide(Lucide.Globe);
export const ListChecks = echelonnerLucide(Lucide.ListChecks);
export const Lock = echelonnerLucide(Lucide.Lock);
export const Mail = echelonnerLucide(Lucide.Mail);
export const MessageCircleQuestion = echelonnerLucide(Lucide.MessageCircleQuestion);
export const Mic = echelonnerLucide(Lucide.Mic);
export const Plus = echelonnerLucide(Lucide.Plus);
export const Send = echelonnerLucide(Lucide.Send);
export const Settings = echelonnerLucide(Lucide.Settings);
export const MapPin = echelonnerLucide(Lucide.MapPin);
export const Search = echelonnerLucide(Lucide.Search);
export const ShieldCheck = echelonnerLucide(Lucide.ShieldCheck);
export const GraduationCap = echelonnerLucide(Lucide.GraduationCap);
export const Info = echelonnerLucide(Lucide.Info);
export const SlidersHorizontal = echelonnerLucide(Lucide.SlidersHorizontal);
export const Sparkles = echelonnerLucide(Lucide.Sparkles);
export const Star = echelonnerLucide(Lucide.Star);
export const Target = echelonnerLucide(Lucide.Target);
export const Trash2 = echelonnerLucide(Lucide.Trash2);
export const TriangleAlert = echelonnerLucide(Lucide.TriangleAlert);
export const Upload = echelonnerLucide(Lucide.Upload);
export const User = echelonnerLucide(Lucide.User);
export const UserRound = echelonnerLucide(Lucide.UserRound);
export const Users = echelonnerLucide(Lucide.Users);
export const Zap = echelonnerLucide(Lucide.Zap);
export const X = echelonnerLucide(Lucide.X);
